import type { Location } from 'history';
import { includes, intersection } from 'lodash';
import type { ComponentType, FC, ReactElement, ReactNode } from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router';

import { Stack } from '@mui/material';

import type { RootState } from 'store';

import { useDCRAccess } from 'common/dcr/utils/useDCRAccess';
import { RoleVariantEnum, UserTypes } from 'common/graphql/types';
import { useIsClinicalTrialLeadUser } from 'common/hooks/useIsClinicalTrialLeadUser';
import { OfflineManager } from 'common/native-app-support/native.offline.manager';
import type { RouteType } from 'common/types/route';
import { ECONSENT_REGEX, STUDY_ID_REGEX, STUDY_VERSION_URL_REGEX } from 'common/utils/constants';
import { Loading } from 'common/utils/loading';

import { isUserLoggedIn } from './auth_role_permissions_utils';
import StudyRoute from './auth_study_restrict';
import useStudySiteQueryParamUpdate from './hooks/useStudySiteQueryParamUpdate';

interface AccessibleRouteProps {
    location: Location<unknown>;
    component: ComponentType<any>;
}

const AccessibleRoute = ({ component: Component, ...props }: AccessibleRouteProps): ReactElement => {
    const checkStudyDeleted =
        !STUDY_VERSION_URL_REGEX.test(props.location.pathname) && STUDY_ID_REGEX.test(props.location.pathname);

    if (checkStudyDeleted && !ECONSENT_REGEX.test(props.location.pathname)) {
        return (
            <StudyRoute component={Component} studyId={props.location.pathname.match(STUDY_ID_REGEX)[0]} {...props} />
        );
    }

    return <Component {...props} />;
};

const hasPermission = (userPermissions, requiredPermissions): boolean =>
    !!(
        !requiredPermissions ||
        !requiredPermissions.length ||
        intersection(requiredPermissions, userPermissions).length
    );

const hasRole = (userRole, requiredRole): boolean => includes(requiredRole, userRole);

interface Props {
    path: string;
    exact?: boolean;
    component: ReactNode;
    role: string[];
    permissions: string[];
    permsRedirect?: string;
    accessRoute?: RouteType;
    history?: History;
    location?: Location;
}

export const AuthenticatedRoute: FC<Props> = ({
    component,
    role = [],
    permissions = [],
    permsRedirect = '/',
    accessRoute,
    ...rest
}: Props): ReactElement => {
    const auth = useSelector((state: RootState) => state.auth);
    const { loading } = useStudySiteQueryParamUpdate(auth?.activeUser);

    const { canAccessDCR, isLoading: isDCRLoading } = useDCRAccess();
    const isClinicalTrialLeadUser = useIsClinicalTrialLeadUser();

    const getComponentOrRedirect = useCallback(
        (props, accessRoute: RouteType): ReactElement => {
            const isLoggedIn = isUserLoggedIn(auth);

            const roleId =
                auth.activeUser?.roleVClaim?.sk ||
                // Since base role ID of sysadmin and subadmin are the same, differentiate them with role attribute
                (auth.activeUser?.roleBClaim?.sk && auth.activeUser?.role === RoleVariantEnum.SystemAdministrator
                    ? RoleVariantEnum.SystemAdministrator
                    : auth.activeUser?.roleBClaim?.sk) ||
                null;

            let hasRequiredPerms = isLoggedIn ? hasRole(roleId, role) : false;
            hasRequiredPerms =
                isLoggedIn && !hasRequiredPerms && !role
                    ? hasPermission(auth.activeUser.permissions, permissions)
                    : hasRequiredPerms;

            if (isLoggedIn && hasRequiredPerms) {
                if (props.location.pathname === '/dcrDashboard') {
                    if (isDCRLoading) {
                        return (
                            <Stack sx={{ position: 'fixed', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
                                <Loading isLoading={isDCRLoading} />
                            </Stack>
                        );
                    }
                    if (!canAccessDCR && !isClinicalTrialLeadUser) {
                        return (
                            <Redirect
                                to={{
                                    pathname: isClinicalTrialLeadUser ? '/study' : permsRedirect,
                                    state: { target: props.location.pathname },
                                }}
                            />
                        );
                    }
                }

                if (
                    !OfflineManager.getInstance().isPhoneOffline() && // Privacy policy page opening delayed until user login online
                    auth.activeUser.currentPolicy?.[0] &&
                    auth.activeUser.acceptedVersionPP?.toString() !==
                        auth.activeUser.currentPolicy[0].majorVersion.toString() &&
                    props.location.pathname !== '/policy' &&
                    !auth?.switchingUser // Don't redirect if switch user modal is opened, we will open policy inside modal over there
                ) {
                    return <Redirect to={{ pathname: '/policy', state: { target: props.location.pathname } }} />;
                }

                const studySiteReady = auth?.activeStudySite?.activeStudySiteReady;
                const studyId = auth?.activeStudySite.activeStudy?.studyId;
                const siteId = auth?.activeStudySite.activeSite?.siteId;

                if (
                    (!!accessRoute.studySiteRequired && (!studySiteReady || !studyId || !siteId)) ||
                    (accessRoute.studyOnlyRequired && (!studySiteReady || !studyId))
                ) {
                    if (loading) {
                        return (
                            <Stack sx={{ position: 'fixed', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
                                <Loading isLoading />
                            </Stack>
                        );
                    }

                    return (
                        <Redirect
                            to={{
                                pathname: '/activestudysite',
                                state: { target: props.location.pathname },
                            }}
                        />
                    );
                }

                return <AccessibleRoute component={component} {...props} />;
            }

            const state = props.location;
            if (!isLoggedIn) {
                localStorage.setItem('postLoginRedirectUrl', state.pathname + state.search);

                return (
                    <Redirect
                        to={{
                            pathname: '/',
                            state: { target: state.pathname },
                        }}
                    />
                );
            }

            if (auth.activeUser.userType === UserTypes.Patient) {
                return (
                    <Redirect
                        to={{
                            pathname: '/patientAccessDenied',
                            state: { target: props.location.pathname },
                        }}
                    />
                );
            }
            if (roleId === RoleVariantEnum.Librarian) {
                return (
                    <Redirect
                        to={{
                            pathname: '/activitylibrary',
                            state: { target: props.location.pathname },
                        }}
                    />
                );
            }
            if (roleId === RoleVariantEnum.StudyBuilder) {
                return (
                    <Redirect
                        to={{
                            pathname: '/study',
                            state: { target: props.location.pathname },
                        }}
                    />
                );
            }

            return <Redirect to={{ pathname: permsRedirect, state: { target: state.pathname } }} />;
        },
        [auth, isDCRLoading, canAccessDCR, loading]
    );

    return <Route {...rest} render={(props): ReactElement => getComponentOrRedirect(props, accessRoute)} />;
};
